'use client'

import { useReducedMotion } from 'framer-motion'
import { MoonIcon, SunIcon } from 'lucide-react'
import { useTheme } from 'next-themes'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

import { OrderXLogoIcon } from '@/components/icons'
import { Carousel, type CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel'
import { applyPageTheme } from '@/lib/apply-page-theme'
import { cn } from '@/lib/utils'

const CAROUSEL_INTERVAL_MS = 7000
const CAROUSEL_PANEL_BACKGROUND = 'linear-gradient(4.72deg, #182326 0.11%, #0C1215 98.88%)'
const CAROUSEL_LOTTIE_BACKGROUND =
  'linear-gradient(180deg, rgba(12, 13, 14, 0) -9.65%, rgba(34, 82, 101, 0.12) 38.76%, rgba(34, 82, 101, 0.08) 69.91%, rgba(99, 108, 116, 0) 91.21%)'
const CAROUSEL_LOTTIE_BORDER =
  'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.2) 54.34%, rgba(255, 255, 255, 0.01) 100%)'

const AuthCarouselLottie = dynamic(
  () =>
    import('@/features/auth/components/auth/auth-carousel-lottie').then(
      (module) => module.AuthCarouselLottie
    ),
  { ssr: false }
)

type CarouselLottieLayout = 'vertical' | 'horizontal'

interface CarouselSlide {
  image: string
  title: string
  description: string
  lottie?: { src: string; layout: CarouselLottieLayout }
}

const CAROUSEL_SLIDES: CarouselSlide[] = [
  {
    image: '/images/landing/carousel/1.webp',
    title: 'Multi-Asset\nAlgorithmic Trading',
    description:
      'Broad suite of execution algorithms, carefully engineered to reduce slippage and market impact. Including TWAP, VWAP, Smart RFQ, Hide & Take, and more.',
  },
  {
    image: '/images/landing/carousel/2.webp',
    lottie: { src: '/lottie/carousel/2.json', layout: 'vertical' },
    title: 'Automation',
    description:
      'Automate execution and hedging based on any market event, position or portfolio level risk, news flow or economic data.',
  },
  {
    image: '/images/landing/carousel/3.webp',
    title: 'Portfolio Simulation\n& Stress-Testing',
    lottie: { src: '/lottie/carousel/3.json', layout: 'horizontal' },
    description:
      'Real-time portfolio and greeks sensitivity analytics, powered by our proprietary risk engine. Anticipate market shifts, simulate risk scenarios, and neutralize unwanted exposure instantly.',
  },
  {
    image: '/images/landing/carousel/4.webp',
    lottie: { src: '/lottie/carousel/4.json', layout: 'horizontal' },
    title: 'Data\n& Execution Analytics',
    description:
      'Track performance across strategies, venues, and executions in real time. Analyze fills, slippage, and execution quality with granular insights.',
  },
  {
    image: '/images/landing/carousel/5.webp',
    lottie: { src: '/lottie/carousel/5.json', layout: 'vertical' },
    title: 'AI Trade Discovery',
    description:
      'Continuously scan markets to surface actionable trade opportunities. Identify inefficiencies, patterns, and setups aligned with your strategies.',
  },
  {
    image: '/images/landing/carousel/6.webp',
    title: 'Unique Data Sources',
    description:
      'Access differentiated market data, signals, and alternative inputs beyond standard feeds. Incorporate news, on-chain data, and proprietary datasets into your workflow.',
  },
]

export function AuthThemeShell({ children }: { children: React.ReactNode }) {
  const { resolvedTheme, theme, setTheme } = useTheme()
  const activeTheme = theme === 'system' ? resolvedTheme : theme
  const isDark = activeTheme === 'dark'
  // Onboarding (esp. the plans step) needs the full width, so it drops the
  // split-screen carousel and renders edge-to-edge.
  const pathname = usePathname()
  const isOnboarding = pathname?.startsWith('/onboarding') ?? false
  const shouldReduceMotion = useReducedMotion()
  const [carouselApi, setCarouselApi] = useState<CarouselApi>()
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [isCarouselHovered, setIsCarouselHovered] = useState(false)

  const toggleTheme = () => {
    const nextTheme = isDark ? 'light' : 'dark'
    applyPageTheme(nextTheme)
    setTheme(nextTheme)
  }

  const selectSlide = useCallback((index: number) => carouselApi?.scrollTo(index), [carouselApi])

  useEffect(() => {
    if (!carouselApi) return

    const syncSelectedSlide = () => {
      setActiveSlideIndex(carouselApi.selectedScrollSnap())
    }

    syncSelectedSlide()
    carouselApi.on('select', syncSelectedSlide)
    carouselApi.on('reInit', syncSelectedSlide)

    return () => {
      carouselApi.off('select', syncSelectedSlide)
      carouselApi.off('reInit', syncSelectedSlide)
    }
  }, [carouselApi])

  useEffect(() => {
    if (!carouselApi || shouldReduceMotion || isCarouselHovered) return

    // A backgrounded tab throttles setInterval rather than pausing it, so the
    // callback can queue up multiple fires while hidden. Returning to the tab
    // then runs them back-to-back, spinning through several slides at once.
    // Tear the interval down while hidden and only restart it once visible again.
    let intervalId: number | null = null

    const start = () => {
      if (intervalId !== null) return
      intervalId = window.setInterval(() => {
        carouselApi.scrollNext()
      }, CAROUSEL_INTERVAL_MS)
    }

    const stop = () => {
      if (intervalId === null) return
      window.clearInterval(intervalId)
      intervalId = null
    }

    const handleVisibilityChange = () => {
      if (document.hidden) stop()
      else start()
    }

    if (!document.hidden) start()
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      stop()
    }
  }, [carouselApi, isCarouselHovered, shouldReduceMotion])

  return (
    <main className="bg-background text-foreground relative flex min-h-dvh w-full flex-col">
      <button
        type="button"
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        onClick={toggleTheme}
        className={cn(
          'absolute top-4 right-4 z-50 flex h-9 items-center gap-1 border px-1.5 text-xs font-medium backdrop-blur-xl transition-colors',
          isDark
            ? 'border-white/15 bg-white/10 text-white shadow-[0_12px_40px_rgba(0,0,0,0.45)] hover:bg-white/15'
            : 'bg-card/70 hover:bg-card/90 border-black/10 text-black shadow-[0_12px_40px_rgba(0,0,0,0.12)]'
        )}
      >
        <span
          className={cn(
            'flex h-6.5 items-center gap-1 px-2 transition-colors',
            !isDark ? 'bg-card text-black shadow-sm' : 'text-white/45'
          )}
        >
          <SunIcon className="size-3.5" />
        </span>
        <span
          className={cn(
            'flex h-6.5 items-center gap-1 px-2 transition-colors',
            isDark ? 'bg-background/70 text-white shadow-sm' : 'text-black/45'
          )}
        >
          <MoonIcon className="size-3.5" />
        </span>
      </button>
      <div
        className={cn(
          'grid min-h-dvh w-full place-items-center lg:place-items-stretch',
          !isOnboarding && 'lg:grid-cols-[minmax(0,1.414fr)_minmax(0,1fr)]'
        )}
      >
        {!isOnboarding && (
          <div className="bg-background relative z-0 hidden min-h-dvh p-4 lg:block">
            <div
              className="relative h-full min-h-[calc(100dvh-32px)] overflow-hidden"
              style={{ background: CAROUSEL_PANEL_BACKGROUND }}
              onMouseEnter={() => setIsCarouselHovered(true)}
              onMouseLeave={() => setIsCarouselHovered(false)}
            >
              <Carousel
                opts={{ align: 'start', loop: true, duration: shouldReduceMotion ? 0 : 32 }}
                setApi={setCarouselApi}
                className="absolute inset-0 [&>div]:h-full"
              >
                <CarouselContent className="ml-0 h-full">
                  {CAROUSEL_SLIDES.map((slide, index) => (
                    <CarouselItem key={slide.image} className="relative h-full pl-0">
                      {slide?.lottie ? (
                        <div
                          className="flex size-full items-start justify-center"
                          style={{ background: CAROUSEL_LOTTIE_BACKGROUND }}
                        >
                          <div
                            className={cn(
                              slide.lottie.layout === 'vertical'
                                ? 'h-[84%] w-[65%]'
                                : 'auth-carousel-lottie-horizontal'
                            )}
                            style={
                              slide.lottie.layout === 'vertical'
                                ? {
                                    border: '2.06px solid transparent',
                                    borderImageSource: CAROUSEL_LOTTIE_BORDER,
                                    borderImageSlice: 1,
                                  }
                                : undefined
                            }
                          >
                            <AuthCarouselLottie
                              src={slide.lottie.src}
                              active={activeSlideIndex === index}
                              reduceMotion={shouldReduceMotion ?? false}
                            />
                          </div>
                        </div>
                      ) : (
                        <Image
                          src={slide.image}
                          alt=""
                          width={1048}
                          height={1044}
                          priority={index === 0}
                          draggable={false}
                          className="size-full object-contain select-none"
                          sizes="58vw"
                        />
                      )}
                      <OrderXLogoIcon
                        aria-hidden="true"
                        className="pointer-events-none absolute top-12 left-20 z-20 h-7 w-auto"
                        style={{ color: '#FFFFFF', fill: '#FFFFFF' }}
                      />
                      <div
                        className="pointer-events-none absolute right-[3.5%] bottom-[10%] left-[4%] z-10 grid grid-cols-[minmax(0,1fr)_minmax(280px,0.84fr)] items-start gap-12"
                        style={{ color: '#FFFFFF' }}
                      >
                        <h2
                          className="text-4xl leading-[120%] font-extralight whitespace-pre-line"
                          style={{
                            fontFamily: "'Test Tiempos Headline', var(--font-inter), serif",
                          }}
                        >
                          {slide.title}
                        </h2>
                        <p className="font-geist max-w-130 text-lg leading-[130%] font-normal tracking-[1%]">
                          {slide.description}
                        </p>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
              <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
                {CAROUSEL_SLIDES.map((slide, index) => (
                  <button
                    key={slide.image}
                    type="button"
                    aria-label={`Show auth preview ${index + 1}`}
                    aria-current={activeSlideIndex === index}
                    onClick={() => selectSlide(index)}
                    className={cn(
                      'h-1 rounded-full transition-all duration-300',
                      activeSlideIndex === index
                        ? 'bg-emphasis w-5'
                        : 'bg-foreground/30 hover:bg-emphasis w-4'
                    )}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        <div className="bg-background z-1 flex min-h-dvh w-full items-center justify-center px-6 py-10">
          {children}
        </div>
      </div>
    </main>
  )
}
